<template>
    <Breadcrumbs main="Roles" :title="isEditing ? 'Edit Role' : 'Create Role'" />
  
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h5>{{ isEditing ? 'Edit Role' : 'Create Role' }}</h5>
            </div>
            <div class="card-body">
              <form @submit.prevent="saveRole">
                <div class="mb-3">
                  <label class="form-label">Role Name</label>
                  <input type="text" class="form-control" v-model="form.name" required />
                </div>
                <div class="mb-3">
                  <label class="form-label">Description</label>
                  <textarea class="form-control" v-model="form.description"></textarea>
                </div>
  
                <hr />
                <h5>Permissions</h5>
                <div class="row">
                    <div class="col-md-12 mb-3">
                        <button type="button" class="btn btn-light-success" @click="addPermission">Add Permission</button>
                    </div>
                    <template v-for="(permission, index) in form.permissions_attributes" :key="index">
                        <div class="col-md-12" v-if="!permission._destroy">
                            <div class="row align-items-end mb-2">
                                <div class="col-md-4">
                                    <label class="form-label">Resource</label>
                                    <select class="form-select" v-model="permission.subject_class">
                                        <option value="all">All</option>
                                        <option v-for="res in resources" :key="res" :value="res">{{ res }}</option>
                                    </select>
                                </div>
                                <div class="col-md-4">
                                    <label class="form-label">Action</label>
                                    <select class="form-select" v-model="permission.action">
                                        <option value="manage">Manage (All Actions)</option>
                                        <option value="read">Read</option>
                                        <option value="create">Create</option>
                                        <option value="update">Update</option>
                                        <option value="destroy">Delete</option>
                                    </select>
                                </div>
                                <div class="col-md-2">
                                    <label class="form-label">&nbsp;</label>
                                    <button type="button" class="btn btn-light-danger d-block" @click="removePermission(index)">
                                        <vue-feather type="trash" size="1.2rem" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
  
                <div class="mt-4">
                  <button type="submit" class="btn btn-light-primary me-2">{{ isEditing ? 'Update' : 'Create' }}</button>
                  <router-link to="/roles" class="btn btn-light-danger">Cancel</router-link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { create_role, get_role, update_role } from '@/services/roles';
  import Swal from 'sweetalert2';
  
  const route = useRoute();
  const router = useRouter();
  
  const form = ref({
    name: '',
    description: '',
    permissions_attributes: []
  });
  
  const resources = [
    'User', 'Child', 'Payment', 'Subscription', 'Material', 'Intervention', 
    'Assessment', 'Report', 'Ticket', 'Faq', 'Notification', 'Option', 
    'Trait', 'Term', 'Indicator', 'Characteristic', 'Role'
  ];
  
  const isEditing = computed(() => !!route.params.id);
  
  const addPermission = () => {
      form.value.permissions_attributes.unshift({
          subject_class: 'all',
          action: 'read'
      });
  };

  const removePermission = (index) => {
    if (form.value.permissions_attributes[index].id){
        form.value.permissions_attributes[index]._destroy = true
    }else{
        form.value.permissions_attributes.splice(index, 1)
    }
  }
  
  const fetchRole = async () => {
    if (isEditing.value) {
      const response = await get_role(route.params.id);
      if (!response.error) {
        const roleData = response.data.data;
        const included = response.data.included || [];
        
        form.value = {
            name: roleData.attributes.name,
            description: roleData.attributes.description,
            permissions_attributes: []
        };

        // Map included permissions
        if (roleData.relationships && roleData.relationships.permissions) {
            const permissionIds = roleData.relationships.permissions.data.map(p => p.id);
            const rolePermissions = included.filter(inc => inc.type === 'permission' && permissionIds.includes(inc.id));
            
            form.value.permissions_attributes = rolePermissions.map(p => ({
                id: p.id,
                subject_class: p.attributes.subject_class,
                action: p.attributes.action,
                _destroy: false
            }));
        }
      }
    }
  };
  
  const saveRole = async () => {
    let response;
    // Filter out empty permissions if any, or handle validation
    
    if (isEditing.value) {
      response = await update_role(route.params.id, form.value);
    } else {
      response = await create_role(form.value);
    }
  
    if (!response.error) {
      Swal.fire('Success!', `Role ${isEditing.value ? 'updated' : 'created'} successfully.`, 'success');
      router.push('/roles');
    } else {
      Swal.fire('Error!', 'Failed to save role.', 'error');
    }
  };
  
  onMounted(() => {
    fetchRole();
  });
  </script>
